import { useState, useEffect, useCallback, useRef } from "react";
import { Box, Tabs, Badge } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import InterestRequestsPanel, {
  filterVisibleInterestRequests,
} from "@/pages/interests/components/InterestRequestsPanel";
import TitleComponent from "@/components/shared/TitleComponent";
import AppHeaderUserActions from "@/components/shared/AppHeaderUserActions";
import { useUserContext } from "@/context/UserContext";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  NAV_TAB_LABEL_FZ,
  navyProfileNavTabBorderRadius,
} from "@/shared/styles/navTabsLayout";
import { useNavTabsStackLayout } from "@/shared/styles/useNavTabsStackLayout";
import PaymentMethodModal from "@/pages/interests/components/PaymentMethodModal";
import CollaborationPaymentSuccessModal from "@/pages/interests/components/CollaborationPaymentSuccessModal";
import { useCollaborationEngagementPayment } from "@/pages/interests/hooks/useCollaborationEngagementPayment";
import { navigateToCompanyWorkspace } from "@/shared/utils/collaborationNavigation.util";
export default function InterestsPage() {
  const navigate = useNavigate();
  const { refreshPendingInterestCount, refreshCollaborationSidebarCounts } = useUserContext();
  const [activeTab, setActiveTab] = useState<string | null>("incoming");
  const {
    isMobile: isProfileNavMobile,
    tabsListStyles: profileNavTabsListStyles,
    tabsRootStyles: profileNavTabsRootStyles,
  } = useNavTabsStackLayout(2);

  // ── incoming ────────────────────────────────────────────────────────────────
  const [incomingRequests, setIncomingRequests] = useState<any[]>([]);
  const [incomingPage, setIncomingPage] = useState(1);
  const [incomingTotalPages, setIncomingTotalPages] = useState(1);
  const [incomingTotalCount, setIncomingTotalCount] = useState(0);
  const [loadingIncoming, setLoadingIncoming] = useState(true);

  // ── sent ────────────────────────────────────────────────────────────────────
  const [sentRequests, setSentRequests] = useState<any[]>([]);
  const [sentPage, setSentPage] = useState(1);
  const [sentTotalPages, setSentTotalPages] = useState(1);
  const [sentTotalCount, setSentTotalCount] = useState(0);
  const [loadingSent, setLoadingSent] = useState(true);

  // ── shared ──────────────────────────────────────────────────────────────────
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // ── fetchers with useCallback for stable references ────────────────────────
  const fetchIncoming = useCallback(async () => {
    setLoadingIncoming(true);
    try {
      const response = await trpc.collaboration.getReceived.query({ page: incomingPage, limit: 10 });
      const data = Array.isArray(response) ? response : response?.matches || [];
      const visible = filterVisibleInterestRequests(data, false);
      setIncomingRequests(visible);
      // UI now shows only pending requests; totals must match what's visible.
      setIncomingTotalPages(visible.length > 0 ? 1 : 1);
      setIncomingTotalCount(visible.length);
    } catch (err) {
      handleTrpcError(err);
      setIncomingRequests([]);
    } finally {
      setLoadingIncoming(false);
    }
  }, [incomingPage]);

  const fetchSent = useCallback(async () => {
    setLoadingSent(true);
    try {
      const response = await trpc.collaboration.getSent.query({ page: sentPage, limit: 10 });
      const data = Array.isArray(response) ? response : response?.matches || [];
      const visible = filterVisibleInterestRequests(data, true);
      setSentRequests(visible);
      setSentTotalPages(visible.length > 0 ? 1 : 1);
      setSentTotalCount(visible.length);
    } catch (err) {
      handleTrpcError(err);
      setSentRequests([]);
    } finally {
      setLoadingSent(false);
    }
  }, [sentPage]);
  // ── ref to track initial mount ─────────────────────────────────────────────
  const isInitialMount = useRef(true);

  // ── smart effect: fetch both on initial load, then only active tab ──────────
  useEffect(() => {
    if (isInitialMount.current) {
      // First load: fetch both datasets to populate badge counts immediately
      fetchIncoming();
      fetchSent();
      isInitialMount.current = false;
    } else {
      // Subsequent renders: fetch only the active tab's data
      if (activeTab === "incoming") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        fetchIncoming();
      } else if (activeTab === "sent") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        fetchSent();
      }
    }
  }, [activeTab, fetchIncoming, fetchSent]);

  const refreshAfterEngagementPayment = useCallback(() => {
    fetchIncoming();
    fetchSent();
    refreshPendingInterestCount();
    refreshCollaborationSidebarCounts();
  }, [fetchIncoming, fetchSent, refreshPendingInterestCount, refreshCollaborationSidebarCounts]);

  const {
    engagementFeeCredits,
    paymentModalOpened,
    paymentLoading,
    beginEngagementPayment,
    handlePaymentModalClose,
    handlePaymentConfirm,
    successData,
    handleSuccessModalClose,
  } = useCollaborationEngagementPayment(refreshAfterEngagementPayment);

  // ── action handlers (unchanged) ─────────────────────────────────────────────
  const handleAccept = async (matchId: string) => {
    setActionLoading(matchId);
    try {
      await trpc.collaboration.accept.mutate({ matchId });
      successNotification({ title: "Success", message: "Collaboration request accepted!" });
      refreshPendingInterestCount();
      refreshCollaborationSidebarCounts();
      navigate(`/collaborations/${matchId}`);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (matchId: string) => {
    setActionLoading(matchId);
    try {
      await trpc.collaboration.reject.mutate({ matchId });
      successNotification({ title: "Success", message: "Collaboration request declined." });
      fetchIncoming();
      refreshPendingInterestCount();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMakePayment = (matchId: string) => {
    beginEngagementPayment(matchId);
  };

  const handleViewProfile = (companyId: string) => {
    void navigateToCompanyWorkspace(navigate, companyId);
  };
  const handleOpenWorkspace = (matchId: string) => navigate(`/collaborations/${matchId}`);

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <Box mih="100vh" pb={20}>
      <TitleComponent
        title="Requests"
        description="Manage incoming and outgoing collaboration requests"
        rightSection={<AppHeaderUserActions />}
      />

      <Tabs value={activeTab} onChange={setActiveTab} p="md" styles={profileNavTabsRootStyles}>
        <Box w="100%" style={NAV_TABS_FULL_WIDTH_LINE_STYLE}>
          <Tabs.List styles={profileNavTabsListStyles}>
          <Tabs.Tab
            value="incoming"
            px={{ base: "md", sm: "lg" }}
            py="xs"
            fz={NAV_TAB_LABEL_FZ}
            style={{
              ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
              backgroundColor: activeTab === "incoming" ? "#04305c" : "#ebf0ff",
              color: activeTab === "incoming" ? "white" : "var(--mantine-color-slate-6)",
              borderRadius: navyProfileNavTabBorderRadius(isProfileNavMobile, "first"),
              border: activeTab === "incoming" ? "1px solid #04305c" : "1px solid #E5E7EB",
              borderBottom: "none",
              fontWeight: activeTab === "incoming" ? 500 : 400,
              cursor: "pointer",
              minHeight: 44,
            }}
            rightSection={
              <Box
                w={28}
                h={28}
                miw={28}
                mih={28}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              >
                <Badge
                  w={24}
                  h={24}
                  p={0}
                  radius="xl"
                  variant="filled"
                  styles={{
                    root: {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 24,
                      backgroundColor: activeTab === "incoming" ? "#ffffff" : "#94a3b8",
                      color: activeTab === "incoming" ? "#04305c" : "#ffffff",
                      fontWeight: 600,
                      fontSize: 11,
                      border: "none",
                    },
                  }}
                >
                  {incomingTotalCount}
                </Badge>
              </Box>
            }
          >
            Received Requests
          </Tabs.Tab>

          <Tabs.Tab
            value="sent"
            px={{ base: "md", sm: "lg" }}
            py="xs"
            fz={NAV_TAB_LABEL_FZ}
            style={{
              ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
              backgroundColor: activeTab === "sent" ? "#04305c" : "#ebf0ff",
              color: activeTab === "sent" ? "white" : "var(--mantine-color-slate-6)",
              borderRadius: navyProfileNavTabBorderRadius(isProfileNavMobile, "last"),
              border: activeTab === "sent" ? "1px solid #04305c" : "1px solid #E5E7EB",
              borderBottom: "none",
              fontWeight: activeTab === "sent" ? 500 : 400,
              cursor: "pointer",
              minHeight: 44,
            }}
            rightSection={
              <Box
                w={28}
                h={28}
                miw={28}
                mih={28}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
              >
                <Badge
                  w={24}
                  h={24}
                  p={0}
                  radius="xl"
                  variant="filled"
                  styles={{
                    root: {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 24,
                      backgroundColor: activeTab === "sent" ? "#ffffff" : "#94a3b8",
                      color: activeTab === "sent" ? "#04305c" : "#ffffff",
                      fontWeight: 600,
                      fontSize: 11,
                      border: "none",
                    },
                  }}
                >
                  {sentTotalCount}
                </Badge>
              </Box>
            }
          >
            Sent Requests
          </Tabs.Tab>
          </Tabs.List>
        </Box>

        <Tabs.Panel value="incoming" pt="xl">
          <InterestRequestsPanel
            loading={loadingIncoming}
            requests={incomingRequests}
            isRequester={false}
            totalPages={incomingTotalPages}
            totalCount={incomingTotalCount}
            page={incomingPage}
            onPageChange={setIncomingPage}
            emptyLabel="No incoming requests"
            actionLoading={actionLoading}
            onAccept={handleAccept}
            onDecline={handleDecline}
            onMakePayment={handleMakePayment}
            onViewProfile={handleViewProfile}
            onOpenWorkspace={handleOpenWorkspace}
          />
        </Tabs.Panel>

        <Tabs.Panel value="sent" pt="xl">
          <InterestRequestsPanel
            loading={loadingSent}
            requests={sentRequests}
            isRequester={true}
            totalPages={sentTotalPages}
            totalCount={sentTotalCount}
            page={sentPage}
            onPageChange={setSentPage}
            emptyLabel="No sent requests"
            actionLoading={actionLoading}
            onAccept={handleAccept}
            onDecline={handleDecline}
            onMakePayment={handleMakePayment}
            onViewProfile={handleViewProfile}
            onOpenWorkspace={handleOpenWorkspace}
          />
        </Tabs.Panel>
      </Tabs>

      <PaymentMethodModal
        opened={paymentModalOpened}
        onClose={handlePaymentModalClose}
        onConfirm={handlePaymentConfirm}
        loading={paymentLoading}
        creditsRequired={engagementFeeCredits}
      />
      <CollaborationPaymentSuccessModal
        opened={!!successData}
        onClose={handleSuccessModalClose}
        paymentData={successData}
      />
    </Box>
  );
}
